import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X, Send, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSENT_KEY = 'enbet_chat_consent_v1';
const PROACTIVE_SESSION_KEY = 'enbet_tom_proactive_fired_v1';
const PROACTIVE_SCROLL_THRESHOLD = 0.4;
const PROACTIVE_TIME_MS = 20000;

const PROACTIVE_PAGES = {
  '/': {
    de: 'Hey, schön dass du da bist. Ist das Thema KI gerade relevant für dich?',
    en: "Hey, glad you're here. Is AI a relevant topic for you right now?",
  },
  '/ki-ready-check': {
    de: 'Hey, du schaust dir gerade unseren KI Ready Check an. Hast du konkrete Fragen dazu?',
    en: "Hey, you're checking out our KI Ready Check. Any specific questions?",
  },
  '/ki-strategie-workshop': {
    de: 'Hey, du schaust dir unseren Strategie-Workshop an. Soll ich kurz erklären wie der Tag abläuft?',
    en: "Hey, you're looking at our Strategy Workshop. Want me to explain how the day works?",
  },
  '/ki-kombi-paket': {
    de: 'Hey, du interessierst dich für unser Kombi-Paket. Magst du wissen warum es sich gegenüber Einzelbuchung lohnt?',
    en: "Hey, you're interested in our Combo Bundle. Want to know why it beats booking separately?",
  },
  '/ki-im-stationaeren-handel': {
    de: 'Hey, du schaust dir KI im stationären Handel an. Hast du eine konkrete Fragestellung aus deinem Filialgeschäft?',
    en: "Hey, you're reading about AI in brick-and-mortar retail. Any specific question from your store operations?",
  },
  '/ki-fuer-filialisten': {
    de: 'Hey, du schaust dir KI für Filialisten an. Wie viele Standorte hast du denn?',
    en: "Hey, you're looking at AI for chain retailers. How many locations do you run?",
  },
  '/ki-strategie-retail': {
    de: 'Hey, du schaust dir KI-Strategie im Retail an. Was ist deine größte Baustelle gerade?',
    en: "Hey, you're reading about AI strategy in retail. What's your biggest open issue right now?",
  },
  '/ki-beratung-kassel': {
    de: 'Hey, schön dass du auf uns aufmerksam geworden bist. Suchst du KI-Beratung im Raum Kassel?',
    en: 'Hey, glad you found us. Looking for AI consulting near Kassel?',
  },
};

const TEXTS = {
  de: {
    initial: 'Hey! Ich bin Tom von EnBet Consulting. Wie heißt du?',
    headerTitle: 'Tom — KI-Assistent',
    openAria: 'Chat öffnen',
    consentWelcome: 'Willkommen beim KI-Assistenten von EnBet Consulting.',
    consentText: 'Um den Chat zu nutzen, stimme bitte unserer ',
    consentPrivacyLink: 'Datenschutzerklärung',
    consentSuffix: ' zu. Darin erklären wir, wie deine Daten im Rahmen dieses Gesprächs verarbeitet werden.',
    consentButton: 'AGB zustimmen & Chat starten',
    inputPlaceholder: 'Nachricht eingeben...',
    bookCalendar: 'Termin buchen',
    bookingConfirmedHeader: 'Termin bestätigt',
    bookingConfirmedText: 'Eine Bestätigung wurde per E-Mail gesendet.',
    meetingLink: 'Meeting-Link',
    error: 'Entschuldigung, ein Fehler ist aufgetreten. Bitte versuche es erneut.',
  },
  en: {
    initial: "Hey! I'm Tom from EnBet Consulting. What's your name?",
    headerTitle: 'Tom — AI Assistant',
    openAria: 'Open chat',
    consentWelcome: "Welcome to EnBet Consulting's AI assistant.",
    consentText: 'To use the chat, please accept our ',
    consentPrivacyLink: 'Privacy Policy',
    consentSuffix: '. It explains how your data is processed during this conversation.',
    consentButton: 'Accept & start chat',
    inputPlaceholder: 'Type a message...',
    bookCalendar: 'Book appointment',
    bookingConfirmedHeader: 'Appointment confirmed',
    bookingConfirmedText: 'A confirmation has been sent via email.',
    meetingLink: 'Meeting link',
    error: 'Sorry, an error occurred. Please try again.',
  },
};

export default function ChatWidget() {
  const { lang } = useLanguage();
  const t = TEXTS[lang] || TEXTS.de;
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [accumulatedLeadData, setAccumulatedLeadData] = useState({});
  const [consented, setConsented] = useState(() => !!localStorage.getItem(CONSENT_KEY));
  const [messages, setMessages] = useState([{ role: 'assistant', content: t.initial }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [proactiveTeaser, setProactiveTeaser] = useState(null);
  const bottomRef = useRef(null);

  // Proaktiver Trigger — feuert wenn 40% gescrollt UND 20s vergangen sind,
  // einmal pro Session pro Browser. Nur auf konfigurierten Pages.
  useEffect(() => {
    if (open) return;
    if (typeof window === 'undefined') return;
    try { if (sessionStorage.getItem(PROACTIVE_SESSION_KEY)) return; } catch { return; }

    const pageConfig = PROACTIVE_PAGES[location.pathname];
    if (!pageConfig) return;

    let scrollReached = false;
    let timeReached = false;
    let fired = false;

    const fire = () => {
      if (fired || !scrollReached || !timeReached) return;
      fired = true;
      const teaserText = pageConfig[lang] || pageConfig.de;
      setProactiveTeaser(teaserText);
      setMessages(prev => {
        const hasUserMsg = prev.some(m => m.role === 'user');
        if (hasUserMsg) return prev;
        return [{ role: 'assistant', content: teaserText }];
      });
      setOpen(true);
      try { sessionStorage.setItem(PROACTIVE_SESSION_KEY, '1'); } catch {}
    };

    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = window.scrollY / docHeight;
      if (pct >= PROACTIVE_SCROLL_THRESHOLD) {
        scrollReached = true;
        fire();
      }
    };

    const timer = setTimeout(() => { timeReached = true; fire(); }, PROACTIVE_TIME_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
  }, [location.pathname, lang, open]);

  // Sync initial greeting when language changes (only if user hasn't started chatting yet)
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 1 && prev[0].role === 'assistant') {
        return [{ role: 'assistant', content: t.initial }];
      }
      return prev;
    });
  }, [lang, t.initial]);

  useEffect(() => {
    if (open && consented) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, consented]);

  const handleConsent = () => {
    localStorage.setItem(CONSENT_KEY, '1');
    setConsented(true);
  };

  const send = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : input).trim();
    if (!text || loading) return;
    if (typeof overrideText !== 'string') setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        message: text,
        history: messages.slice(-10),
        lang,
        accumulated_lead_data: accumulatedLeadData,
        page_path: location.pathname,
      });
      // Build single assistant message with optional alternatives attached
      const assistantMsg = { role: 'assistant', content: data.reply };
      if (data.slot_taken && data.alternatives?.length) {
        assistantMsg.alternatives = data.alternatives;
      }
      setMessages((prev) => [...prev, assistantMsg]);
      if (data.show_calendar) setShowCalendar(true);
      if (data.booking_confirmed) setBookingConfirmed(data.booking_confirmed);
      // Lead-State akkumulieren — Backend hat schon gemerged, einfach übernehmen
      if (data.lead_data) setAccumulatedLeadData(data.lead_data);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t.error },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t.openAria}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#befa0a] text-[#0a0a0a] flex items-center justify-center shadow-xl hover:bg-white transition-colors"
        >
          <MessageSquare size={24} />
        </button>
      )}

      {open && (
        <div className="fixed inset-x-0 bottom-0 md:inset-x-auto md:bottom-6 md:right-6 z-50 flex flex-col bg-[#0a0a0a] border border-[#2a2a2a] shadow-2xl md:w-80 md:rounded-none w-full"
          style={{ height: '90vh', maxHeight: '600px' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a]">
            <div>
              <p className="text-[#befa0a] text-sm font-bold uppercase tracking-widest"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.headerTitle}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#befa0a]" />
                <span className="text-white/40 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  EnBet Consulting
                </span>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Consent Screen */}
          {!consented ? (
            <div className="flex-1 flex flex-col justify-center px-6 py-8">
              {proactiveTeaser && (
                <div className="mb-5 px-3 py-3 bg-[#161616] border border-[#befa0a]/40 text-white text-sm leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {proactiveTeaser}
                </div>
              )}
              <p className="text-white text-sm leading-relaxed mb-6"
                style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t.consentWelcome}<br /><br />
                {t.consentText}
                <button onClick={() => window.dispatchEvent(new Event('openDatenschutz'))}
                  className="text-[#befa0a] hover:underline font-bold underline">
                  {t.consentPrivacyLink}
                </button>{t.consentSuffix}
              </p>
              <button
                onClick={handleConsent}
                className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 text-sm font-bold uppercase tracking-widest hover:bg-white transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {t.consentButton}
              </button>
            </div>

          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className="flex flex-col">
                    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[82%] px-3 py-2 text-sm leading-relaxed whitespace-pre-line ${
                          msg.role === 'user'
                            ? 'bg-[#befa0a] text-[#0a0a0a] font-medium'
                            : 'bg-[#161616] text-white/80 border border-[#252525]'
                        }`}
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {msg.content}
                      </div>
                    </div>
                    {msg.alternatives && (
                      <div className="flex flex-col gap-1 mt-1 ml-0">
                        {msg.alternatives.map((alt, ai) => (
                          <button key={ai} onClick={() => send(alt.label)} disabled={loading}
                            className="text-left text-xs px-3 py-1.5 border border-[#befa0a]/50 text-[#befa0a] hover:bg-[#befa0a] hover:text-[#0a0a0a] transition-colors disabled:opacity-40"
                            style={{ fontFamily: "'Manrope', sans-serif" }}>
                            {alt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-[#161616] border border-[#252525] px-3 py-2 text-white/30 text-sm"
                      style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <span className="animate-pulse">···</span>
                    </div>
                  </div>
                )}

                {showCalendar && (
                  <div className="flex justify-start">
                    <button
                      onClick={() => { setOpen(false); setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 200); }}
                      className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    >
                      <Calendar size={13} />
                      {t.bookCalendar}
                    </button>
                  </div>
                )}

                {bookingConfirmed && (
                  <div className="mx-1 p-3 bg-[#befa0a] text-[#0a0a0a]">
                    <p className="text-xs font-bold uppercase tracking-wider mb-1"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {t.bookingConfirmedHeader}
                    </p>
                    <p className="text-xs leading-snug" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {t.bookingConfirmedText}
                      {bookingConfirmed.meet_link && (
                        <> &nbsp;<a href={bookingConfirmed.meet_link} target="_blank" rel="noopener noreferrer"
                          className="underline font-bold">{t.meetingLink}</a></>
                      )}
                    </p>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-3 border-t border-[#2a2a2a] flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={t.inputPlaceholder}
                  maxLength={1000}
                  className="flex-1 bg-transparent border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#befa0a] placeholder-white/20"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="bg-[#befa0a] text-[#0a0a0a] px-3 py-2 hover:bg-white transition-colors disabled:opacity-30 flex-shrink-0"
                >
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
